import { addChild, addIElements, addToLoadingRoots, removeFromLoadingRoots, removeIElement, selectIElement, setRootId } from "@faro-lotv/project-source";
import { createAsyncThunk } from "@reduxjs/toolkit";

/** Parameters of the fetchProjectIElements thunk */

/**
 * Adds or replaces elements in the store. Unlike `addIElements`, it also adds new elements
 * to the children of their parent, so the selectors that walk down the tree find them.
 */
export const setIElements = createAsyncThunk("ielements/setIElements", (elements, {
  dispatch,
  getState
}) => {
  dispatch(addIElements(elements));
  for (const element of elements) {
    const parent = selectIElement(element.parentId)(getState());
    if (parent && !parent.childrenIds?.includes(element.id)) {
      dispatch(addChild({
        id: parent.id,
        childId: element.id
      }));
    }
  }
});

/**
 * Removes elements from the store. Unlike `removeIElement`, it also updates the parent when only
 * the parent is stored, e.g. attachments, of which only their group is kept in the store.
 */
export const removeIElements = createAsyncThunk("ielements/removeIElements", (elements, {
  dispatch,
  getState
}) => {
  for (const element of elements) {
    const parent = selectIElement(element.parentId)(getState());
    if (parent?.childrenIds?.includes(element.id)) {
      dispatch(addIElements([{
        ...parent,
        childrenIds: parent.childrenIds.filter(id => id !== element.id)
      }]));
    }
    dispatch(removeIElement(element.id));
  }
});

/**
 * Fetch some of the IElements of the project
 * Optionally set an element as the root of the tree for loading indication
 * This is wrapper for the `fetchProjectIElements` async thunk implemented by the lotv team.
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const fetchProjectIElements = createAsyncThunk("ielements/fetchProjectIElements", async ({
  fetcher,
  loadingIds
}, {
  dispatch,
  signal
}) => {
  loadingIds?.map(loadingId => dispatch(addToLoadingRoots(loadingId)));
  let elements = [];
  elements = await fetcher(signal).finally(() => {
    loadingIds?.map(loadingId => dispatch(removeFromLoadingRoots(loadingId)));
  });

  // If the thunk was aborted the elements might be incomplete and might not belong anymore
  // to the selected project, so they should not be added to the store
  if (signal.aborted || !elements.length) {
    return elements;
  }
  const root = elements.find(el => el.id === elements[0].rootId);
  dispatch(addIElements(elements));
  if (root) {
    dispatch(setRootId(root.id));
  }
  return elements;
});